import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { setApiToken, getApiTokenAsync } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const SESSION_USER_KEY = "fm_user_cache";

function getCachedUser() {
  try { const r = sessionStorage.getItem(SESSION_USER_KEY); return r ? JSON.parse(r) : null; } catch { return null; }
}
function setCachedUser(u) {
  try { if (u?.user_id) sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(u)); } catch {}
}
function clearCachedUser() {
  try { sessionStorage.removeItem(SESSION_USER_KEY); } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getCachedUser());
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const fetchedRef = useRef(false);

  const fetchUser = useCallback(async () => {
    try {
      const token = await getApiTokenAsync();
      if (!token) { setUser(null); clearCachedUser(); setLoading(false); return; }
      const { data } = await api.get("/auth/me");
      if (data && data.user_id) {
        setUser(data);
        setCachedUser(data);
      } else {
        setUser(null); clearCachedUser(); setApiToken(null);
      }
    } catch {
      setUser(null); clearCachedUser(); setApiToken(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) { fetchedRef.current = true; fetchUser(); }
  }, [fetchUser]);

  const login = useCallback(async (credentials) => {
    const { data } = await api.post("/auth/login", credentials);
    if (data.token) {
      setApiToken(data.token);
      const me = await api.get("/auth/me");
      setUser(me.data); setCachedUser(me.data);
      setAuthOpen(false);
    }
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) {
      setApiToken(data.token);
      const me = await api.get("/auth/me");
      setUser(me.data); setCachedUser(me.data);
      setAuthOpen(false);
    }
    return data;
  }, []);

  const logout = useCallback(() => {
    setApiToken(null); setUser(null); clearCachedUser();
    try { localStorage.removeItem("fm_token"); } catch {}
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, authOpen, setAuthOpen, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}
